import { gql, useQuery } from "@apollo/client";
import { Button, Classes, Colors } from "@blueprintjs/core";
import format from "date-fns/format";
import id from "date-fns/locale/id/index";
import * as FileSaver from "file-saver";
import { omit } from "lodash";
import React, { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { default as RGL, WidthProvider } from "react-grid-layout";
import * as xlsx from "xlsx";
import client from "../../client";
import PPemprov from "./pemprov/PPemprodifabel";
import SBar from "./SBar";
import SCard from "./SCard";
import PCard from "./pemprov/PCard";
import SPie from "./SPie";

const ReactGridLayout = WidthProvider(RGL);
const fileType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const fileExtension = ".xlsx";

export default function Grafik({ opdId, name }) {
  const [date, setDate] = useState(new Date());

  const excelQueryJatim = {
    query: gql`
      query result($bulan: Int, $tahun: Int) {
        opd(where: { pId: { _eq: "1" } }) {
          id
          pId
          name
          stats_gender(
            where: { _and: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } } }
          ) {
            pria
            wanita
            tahun
            bulan
          }
          stats_umur(
            where: { _and: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } } }
          ) {
            between_20_29
            between_30_39
            between_40_49
            between_50_55
            gt_55
            lt_20
          }
          stats_eselon(
            where: { _and: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } } }
          ) {
            bulan
            tahun
            ia
            ib
            iia
            iib
            iiia
            iiib
            iva
            ivb
          }
          stats_pendidikan(
            where: { _and: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } } }
          ) {
            bulan
            tahun
            sd
            di
            dii
            diii
            div
            s1
            s2
            s3
          }
          stats_golongan(
            where: { _and: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } } }
          ) {
            bulan
            tahun
            gol_i
            gol_ii
            gol_iii
            gol_iv
          }
          stats_jabatan(
            where: { _and: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } } }
          ) {
            bulan
            tahun
            struktural
            pelaksana
            fungsional
          }
        }
      }
    `,
    variables: {
      bulan: format(date, "M"),
      tahun: format(date, "yyyy"),
    },
  };

  const opdQuery = {
    query: gql`
      query opdById($opdId: String!, $tahun: Int, $bulan: Int) {
        opd: opd_by_pk(id: $opdId) {
          id
          name
          pId
          stats_eselon(
            where: {
              _and: [{ bulan: { _eq: $bulan } }, { tahun: { _eq: $tahun } }]
            }
          ) {
            ia
            ib
            iia
            iib
            iiia
            iiib
            iva
            ivb
          }
          stats_golongan(
            where: {
              _and: [{ bulan: { _eq: $bulan } }, { tahun: { _eq: $tahun } }]
            }
          ) {
            gol_i
            gol_ii
            gol_iii
            gol_iv
          }
          stats_jabatan(
            where: {
              _and: [{ bulan: { _eq: $bulan } }, { tahun: { _eq: $tahun } }]
            }
          ) {
            fungsional
            pelaksana
            struktural
          }
          stats_bup(where: { tahun: { _eq: $tahun } }) {
            total
          }
          stats_kp_reguler(where: { tahun: { _eq: $tahun } }) {
            total
          }
          stats_pendidikan(
            where: {
              _and: [{ bulan: { _eq: $bulan } }, { tahun: { _eq: $tahun } }]
            }
          ) {
            sd
            sltp
            slta
            di
            dii
            diii
            div
            s1
            s2
            s3
          }
          stats_gender(
            where: {
              _and: [{ bulan: { _eq: $bulan } }, { tahun: { _eq: $tahun } }]
            }
          ) {
            pria
            wanita
          }
          stats_umur(
            where: {
              _and: [{ bulan: { _eq: $bulan } }, { tahun: { _eq: $tahun } }]
            }
          ) {
            lt_20
            between_20_29
            between_30_39
            between_40_49
            between_50_55
            gt_55
          }
        }
      }
    `,
    variables: {
      opdId,
      bulan: format(date, "M"),
      tahun: format(date, "yyyy"),
    },
  };

  const jatimQuery = {
    query: gql`
      query pemprovJatim($bulan: Int, $tahun: Int) {
        stats_total_pttpk(
          where: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } }
        ) {
          bulan
          tahun
          total
        }
        stats_fungsional_prov_jatim(
          where: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } }
        ) {
          fungsional_medis
          fungsional_guru
          fungsional_lainnya
          fungsional_paramedis
        }
        stats_total_gtt(
          where: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } }
        ) {
          bulan
          tahun
          total
        }
        eselon: stats_eselon_aggregate(
          where: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } }
        ) {
          aggregate {
            sum {
              ia
              ib
              iia
              iib
              iiia
              iiib
              iva
              ivb
            }
          }
        }
        pendidikan: stats_pendidikan_aggregate(
          where: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } }
        ) {
          aggregate {
            sum {
              sd
              sltp
              slta
              di
              dii
              diii
              div
              s1
              s2
              s3
            }
          }
        }
        gender: stats_gender_aggregate(
          where: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } }
        ) {
          aggregate {
            sum {
              pria
              wanita
            }
          }
        }

        kp_reguler: stats_kp_reguler_aggregate(
          where: { tahun: { _eq: $tahun } }
        ) {
          aggregate {
            sum {
              total
            }
          }
        }

        bup: stats_bup_aggregate(where: { tahun: { _eq: $tahun } }) {
          aggregate {
            sum {
              total
            }
          }
        }

        golongan: stats_golongan_aggregate(
          where: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } }
        ) {
          aggregate {
            sum {
              gol_i
              gol_ii
              gol_iii
              gol_iv
            }
          }
        }
        umur: stats_umur_aggregate(
          where: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } }
        ) {
          aggregate {
            sum {
              lt_20
              between_20_29
              between_30_39
              between_40_49
              between_50_55
              gt_55
            }
          }
        }
        jabatan: stats_jabatan_aggregate(
          where: { bulan: { _eq: $bulan }, tahun: { _eq: $tahun } }
        ) {
          aggregate {
            sum {
              pelaksana
              fungsional
              struktural
            }
          }
        }
      }
    `,
    variables: {
      bulan: format(date, "M"),
      tahun: format(date, "yyyy"),
    },
  };

  useEffect(() => {}, [date, opdId]);

  const isPemprov = opdId === "1" || opdId === 1;

  const subtext = `${name} ${format(date, "LLLL", {
    locale: id,
  }).toUpperCase()} TAHUN ${format(date, "yyyy")}`;

  const { loading, data } = useQuery(
    isPemprov ? jatimQuery.query : opdQuery.query,
    {
      variables: isPemprov ? jatimQuery.variables : opdQuery.variables,
    }
  );

  const reportExcelJatim = async () => {
    const {
      data: { opd },
    } = await client.query(excelQueryJatim);

    const arrayGender = opd.map((t) => {
      return {
        "Perangkat Daerah": t.name,
        Wanita: t.stats_gender[0]?.wanita,
        Pria: t.stats_gender[0]?.pria,
        Total: t.stats_gender[0]?.wanita + t.stats_gender[0].pria,
      };
    });

    const arrayEselon = opd.map((t) => {
      return {
        "Perangkat Daerah": t.name,
        Ia: t.stats_eselon[0]?.ia,
        Ib: t.stats_eselon[0]?.ib,
        IIa: t.stats_eselon[0]?.iia,
        IIb: t.stats_eselon[0]?.iib,
        IIIa: t.stats_eselon[0]?.iiia,
        IIIb: t.stats_eselon[0]?.iiib,
        IVa: t.stats_eselon[0]?.iva,
        IVb: t.stats_eselon[0]?.ivb,
      };
    });

    const arrayPendidikan = opd.map((t) => {
      return {
        "Perangkat Daerah": t.name,
        SD: t.stats_pendidikan[0]?.sd,
        DI: t.stats_pendidikan[0]?.di,
        DII: t.stats_pendidikan[0]?.dii,
        DIII: t.stats_pendidikan[0]?.diii,
        DIV: t.stats_pendidikan[0]?.div,
        S1: t.stats_pendidikan[0]?.s1,
        S2: t.stats_pendidikan[0]?.s2,
        S3: t.stats_pendidikan[0]?.s3,
      };
    });

    const arrayGolongan = opd.map((t) => {
      return {
        "Perangkat Daerah": t.name,
        "GOL I": t.stats_golongan[0]?.gol_i,
        "GOL II": t.stats_golongan[0]?.gol_ii,
        "GOL III": t.stats_golongan[0]?.gol_iii,
        "GOL IV": t.stats_golongan[0]?.gol_iv,
      };
    });

    const arrayJabatan = opd.map((t) => {
      return {
        "Perangkat Daerah": t.name,
        Pelaksana: t.stats_jabatan[0]?.pelaksana,
        Struktural: t.stats_jabatan[0]?.struktural,
        Fungsional: t.stats_jabatan[0]?.fungsional,
      };
    });

    const arrayUsia = opd.map((t) => {
      return {
        "Perangkat Daerah": t.name,
        "< 20": t.stats_umur[0].lt_20,
        "20 - 29": t.stats_umur[0].between_20_29,
        "30 - 39": t.stats_umur[0].between_30_39,
        "40 - 49": t.stats_umur[0].between_40_49,
        "> 55": t.stats_umur[0].gt_55,
      };
    });

    const ws = (data) => {
      return xlsx.utils.json_to_sheet(data);
    };

    const wb = {
      Sheets: {
        "Jenis Kelamin": ws(arrayGender),
        Eselon: ws(arrayEselon),
        Usia: ws(arrayUsia),
        Pendidikan: ws(arrayPendidikan),
        Golongan: ws(arrayGolongan),
        "Jenis Jabatan": ws(arrayJabatan),
      },
      SheetNames: [
        "Jenis Kelamin",
        "Eselon",
        "Usia",
        "Pendidikan",
        "Golongan",
        "Jenis Jabatan",
      ],
    };

    const excelBuffer = xlsx.write(wb, { bookType: "xlsx", type: "array" });
    const result = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(result, subtext + fileExtension);
  };

  const reportExcel = () => {
    const currentData = data?.opd;
    const [stats_bup] = currentData.stats_bup;
    const [stats_eselon] = currentData.stats_eselon;
    const [stats_gender] = currentData.stats_gender;
    const [stats_golongan] = currentData.stats_golongan;
    const [stats_jabatan] = currentData.stats_jabatan;
    const [stats_kp_reguler] = currentData.stats_kp_reguler;
    const [stats_pendidikan] = currentData.stats_pendidikan;
    const [stats_umur] = currentData.stats_umur;

    const ws = (data) => {
      return xlsx.utils.json_to_sheet([omit(data, ["__typename"])]);
    };

    const wb = {
      Sheets: {
        "Batas Usia Pensiun": ws(stats_bup),
        Eselon: ws(stats_eselon),
        "Jenis Kelamin": ws(stats_gender),
        Golongan: ws(stats_golongan),
        Jabatan: ws(stats_jabatan),
        "Kenaikan Pangkat": ws(stats_kp_reguler),
        Pendidikan: ws(stats_pendidikan),
        Umur: ws(stats_umur),
      },
      SheetNames: [
        "Batas Usia Pensiun",
        "Eselon",
        "Jenis Kelamin",
        "Golongan",
        "Jabatan",
        "Kenaikan Pangkat",
        "Pendidikan",
        "Umur",
      ],
    };
    const excelBuffer = xlsx.write(wb, { bookType: "xlsx", type: "array" });
    const result = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(result, subtext + fileExtension);
  };

  return (
    <React.Fragment>
      <div>
        <h1 style={{ color: Colors.COBALT1 }}>{name}</h1>
        <p className={Classes.BLOCKQUOTE}>
          Berikut adalah statistik yang ada pada {name}
        </p>
        <span>
          <p>Tanggal Data</p>
          <ReactDatePicker
            selected={date}
            onChange={(date) => setDate(date)}
            dateFormat="MM/yyyy"
            showMonthYearPicker
            showFullMonthYearPicker
          />
        </span>
      </div>
      <br />

      <div>
        {isPemprov ? (
          <React.Fragment>
            <Button onClick={reportExcelJatim}>Report</Button>
            <PPemprov
              data={data}
              loading={loading}
              name={name}
              subtext={subtext}
              tahun={format(date, "yyyy")}
            />
          </React.Fragment>
        ) : (
          <div>
            <Button onClick={reportExcel}>Export</Button>
            
            <ReactGridLayout className="layout" cols={12} rowHeight={30}>
            <div key="a" data-grid={{ x: 0, y: 0, w: 3, h: 3, static: true }}>
                <SCard
                  text="Pegawai"
                  loading={loading}
                  total={0}
                />
              </div>
              <div key="b" data-grid={{ x: 3, y: 0, w: 3, h: 3, static: true }}>
                <SCard
                  text="Jabatan Struktural"
                  total={0}
                  loading={loading}
                  secondaryColor="struktural"
                />
              </div>
              <div key="c" data-grid={{ x: 6, y: 0, w: 3, h: 3, static: true }}>
                <SCard
                  text="Jabatan Pelaksana"
                  total={0}
                  loading={loading}
                  secondaryColor="pelaksana"
                />
              </div>
              <div key="d" data-grid={{ x: 9, y: 0, w: 3, h: 3, static: true }}>
                <SCard
                  text="Jabatan Fungsional"
                  total={0}
                  loading={loading}
                  secondaryColor="fungsional"
                />
              </div>
              
      </ReactGridLayout>
      <ReactGridLayout className="layout" cols={12} rowHeight={30}>
      <div key="bc" data-grid={{ x: 0, y: 0, w: 3, h: 3, static: true }}>
        <PCard
                text="Disabilitas Sensorik"
                secondaryColor="fungsional"
                loading={loading}
                total={0}
              />
        </div>
        <div key="cb" data-grid={{ x: 3, y: 0, w: 3, h: 3, static: true }}>
        <PCard
                text="Disabilitas Intelektual"
                secondaryColor="fungsional"
                loading={loading}
                total={0}
              />
        </div>
        <div key="de" data-grid={{ x: 6, y: 0, w: 3, h: 3, static: true }}>
        <PCard
                text="Disabilitas Mental"
                secondaryColor="fungsional"
                loading={loading}
                total={0}
              />
        </div>
        <div key="ef" data-grid={{ x: 9, y: 0, w: 3, h: 3, static: true }}>
        <PCard
                text="Disabilitas Fisik"
                secondaryColor="fungsional"
                loading={loading}
                total={0}
              />
        </div>
      </ReactGridLayout>
            <ReactGridLayout className="layout" cols={12} rowHeight={30}>                
              <div key="e" data-grid={{ x: 0, y: 0, w: 6, h: 7, static: true }}>
                <SBar
                  subText={subtext}
                  color={Colors.VIOLET5}
                  title="Jenis Kelamin"
                  loading={loading}
                  data={data?.opd?.stats_gender}
                />
              </div>
              
              <div key="g" data-grid={{ x: 6, y: 0, w: 6, h: 7, static: true }}>
                <SBar
                  title="Eselon"
                  color={Colors.GREEN1}
                  subText={subtext}
                  loading={loading}
                  data={data?.opd?.stats_eselon}
                  list={[
                    "Ia",
                    "Ib",
                    "IIa",
                    "IIb",
                    "IIIa",
                    "IIIb",
                    "IVa",
                    "IVb",
                  ]}
                />
              </div>
              <div
                key="f"
                data-grid={{ x: 0, y: 7, w: 12, h: 12, static: true }}
              >
                <SPie
                  title="Usia"
                  subText={subtext}
                  loading={loading}
                  data={data?.opd?.stats_umur}
                />
              </div>
              <div
                key="h"
                data-grid={{ x: 0, y: 19, w: 12, h: 8, static: true }}
              >
                <SBar
                  title="Pendidikan"
                  color={Colors.RED4}
                  subText={subtext}
                  loading={loading}
                  data={data?.opd?.stats_pendidikan}
                />
              </div>
              <div
                key="i"
                data-grid={{ x: 0, y: 27, w: 12, h: 8, static: true }}
              >
                <SBar
                  title="Golongan"
                  subText={subtext}
                  color={Colors.GREEN4}
                  loading={loading}
                  data={data?.opd?.stats_golongan}
                />
              </div>
              {/* <div
                key="j"
                data-grid={{ x: 0, y: 38, w: 6, h: 3, static: true }}
              >
                <SCard
                  text={`Prediksi Batas Usia Pensiun Tahun 000 ${format(
                    date,
                    "yyyy"
                  )}`}
                  total={data?.opd?.stats_bup[0]?.total}
                  loading={loading}
                  secondaryColor={true}
                />
              </div>
              <div
                key="k"
                data-grid={{ x: 6, y: 38, w: 6, h: 3, static: true }}
              >
                <SCard
                  text={`Prediksi Kenaikan Pangkat Tahun ${format(
                    date,
                    "yyyy"
                  )}`}
                  total={data?.opd?.stats_kp_reguler[0]?.total}
                  loading={loading}
                  secondaryColor={true}
                />
              </div> */}
            </ReactGridLayout>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}
